import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthGate } from '@/components/layout/AuthGate';
import { Layout } from '@/components/layout/Layout';
import { Spinner } from '@/components/ui/Spinner';
import { ToastHost } from '@/components/ui/Toast';
// Login + Signup + NotFound stay eager — they're the cold-start entry
// points (or in NotFound's case, render on bad URLs without a chance to
// suspend). Everything else is route-split so the dashboard JS bundle no
// longer ships fabric.js, ag-grid, echarts on a first-page-load.
import { Login } from '@/pages/Login';
import { Signup } from '@/pages/Signup';
import { NotFound } from '@/pages/NotFound';

// `React.lazy` requires a default export. The page files use named
// exports for IDE-jump ergonomics, so we adapt each promise. The
// adapter has zero runtime cost — Vite re-exports under the hood.
const Dashboard       = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Devices         = lazy(() => import('@/pages/Devices').then(m => ({ default: m.Devices })));
const DeviceDetail    = lazy(() => import('@/pages/Devices').then(m => ({ default: m.DeviceDetail })));
const Cameras         = lazy(() => import('@/pages/Cameras').then(m => ({ default: m.Cameras })));
const CamerasConfig   = lazy(() => import('@/pages/CamerasConfig').then(m => ({ default: m.CamerasConfig })));
const CameraSettings  = lazy(() => import('@/pages/CameraSettings').then(m => ({ default: m.CameraSettings })));
const Alerts          = lazy(() => import('@/pages/Alerts').then(m => ({ default: m.Alerts })));
const Analytics       = lazy(() => import('@/pages/Analytics').then(m => ({ default: m.Analytics })));
const Terms           = lazy(() => import('@/pages/Legal').then(m => ({ default: m.Terms })));
const Privacy         = lazy(() => import('@/pages/Legal').then(m => ({ default: m.Privacy })));
// Guide & Troubleshooting — PUBLIC, full-page version of the in-app guide.
// Opened in a new tab from camera settings (and the sidebar). Public + outside
// AuthGate so it opens instantly in a fresh tab; it's static help content with
// no API calls. Reads ?topic= to deep-link a section.
const Guide           = lazy(() => import('@/pages/Guide').then(m => ({ default: m.Guide })));
const Profile         = lazy(() => import('@/pages/Profile').then(m => ({ default: m.Profile })));
const Billing         = lazy(() => import('@/pages/Billing').then(m => ({ default: m.Billing })));
const Faces           = lazy(() => import('@/pages/Faces').then(m => ({ default: m.Faces })));
// Public landing page for the email-verification link the backend sends
// in the welcome email. Sits OUTSIDE AuthGate because users arrive from
// their inbox in a fresh tab with no signed-in session.
const VerifyEmail     = lazy(() => import('@/pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
// Password recovery pages — both public, both reached from /login or
// from the backend-sent reset email. /reset-password reads `?token=` so
// it MUST be a top-level route (not under AuthGate) because the link
// arrives in a clean tab without a signed-in session.
const ForgotPassword  = lazy(() => import('@/pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword   = lazy(() => import('@/pages/ResetPassword').then(m => ({ default: m.ResetPassword })));

// Lightweight full-page spinner shown while a route chunk is fetching.
// Keep this dependency-free so it's part of the eager bundle. A real
// production app could add a min-display delay (`useDelayedFlag`) to
// prevent flash-on-fast-network — overkill for now.
const RouteFallback = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spinner size={28} />
  </div>
);

export const App = () => {
  return (
    <BrowserRouter>
      {/*
        ToastHost is mounted ONCE at the app root so it sits above every
        Route + Modal stacking context. The store (useToastStore) is the
        global queue; ToastHost subscribes and renders via createPortal to
        document.body — placement OUTSIDE the Suspense boundary keeps
        toasts visible even while a route chunk is fetching.
      */}
      <ToastHost />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/guide" element={<Guide />} />

          <Route
            element={
              <AuthGate>
                <Layout />
              </AuthGate>
            }
          >
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/devices/:id" element={<DeviceDetail />} />
            <Route path="/cameras" element={<Cameras />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/config/cameras" element={<CamerasConfig />} />
            <Route path="/config/cameras/:id" element={<CameraSettings />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/faces" element={<Faces />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};
